import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { sportsbookNames, formatMarketType } from '../utils/oddsUtils';

function MultiSelectDropdown({ label, options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const allSelected = selected.length === 0;

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter(v => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '5px 10px',
          backgroundColor: allSelected ? '#1a1f2e' : 'rgba(0, 217, 255, 0.1)',
          border: `1px solid ${allSelected ? '#2d3748' : '#00d9ff'}`,
          borderRadius: '6px',
          color: allSelected ? '#a0aec0' : '#00d9ff',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          whiteSpace: 'nowrap'
        }}
      >
        {allSelected ? label : `${label} (${selected.length})`}
        <span style={{ fontSize: '10px' }}>▾</span>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          zIndex: 200,
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '6px',
          padding: '4px 0',
          minWidth: '170px',
          maxHeight: '220px',
          overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
        }}>
          <div
            onClick={() => { onChange([]); setIsOpen(false); }}
            style={{
              padding: '7px 12px',
              fontSize: '12px',
              color: allSelected ? '#00d9ff' : '#a0aec0',
              fontWeight: allSelected ? '700' : '400',
              cursor: 'pointer',
              borderBottom: '1px solid #2d3748'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0f1419'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            All
          </div>
          {options.map(opt => {
            const isSelected = selected.includes(opt.value);
            return (
              <div
                key={opt.value}
                onClick={() => toggle(opt.value)}
                style={{
                  padding: '7px 12px',
                  fontSize: '12px',
                  color: isSelected ? '#00d9ff' : '#a0aec0',
                  fontWeight: isSelected ? '600' : '400',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0f1419'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{
                  width: '13px',
                  height: '13px',
                  border: `1px solid ${isSelected ? '#00d9ff' : '#4a5568'}`,
                  borderRadius: '3px',
                  backgroundColor: isSelected ? '#00d9ff' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '9px',
                  color: '#000',
                  fontWeight: 'bold'
                }}>
                  {isSelected && '✓'}
                </span>
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const DATE_OPTIONS = [
  { label: 'Today', value: 0 },
  { label: '+1d', value: 1 },
  { label: '+2d', value: 2 },
  { label: '+3d', value: 3 },
];

function FilterBar({
  availableLeagues = [],
  availableBooks = [],
  availableKeytypes = [],
  selectedLeagues,
  selectedBooks,
  selectedKeytypes,
  dateLimit,
  setSelectedLeagues,
  setSelectedBooks,
  setSelectedKeytypes,
  setDateLimit,
  showOnlyMyBooks,
  setShowOnlyMyBooks
}) {
  const { user } = useUser();
  const myBooks = user?.unsafeMetadata?.selectedBooks || [];
  const [showPrompt, setShowPrompt] = useState(false);

  const handleMyBooksToggle = () => {
    if (!showOnlyMyBooks) {
      if (myBooks.length === 0) {
        setShowPrompt(true);
        return;
      }
    }
    setShowOnlyMyBooks(!showOnlyMyBooks);
    setShowPrompt(false);
  };

  const hasActive = selectedLeagues.length > 0 || selectedBooks.length > 0 || selectedKeytypes.length > 0 || dateLimit !== null || showOnlyMyBooks;

  const leagueOpts = availableLeagues.map(l => ({ value: l, label: l }));
  const bookOpts = availableBooks.map(b => ({ value: b, label: sportsbookNames[b] || b }));
  const keytypeOpts = availableKeytypes.map(k => ({ value: k, label: formatMarketType(k) }));

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
      padding: '10px 0',
      borderBottom: '1px solid #2d3748',
      marginBottom: '12px'
    }}>
      <span style={{
        fontSize: '11px',
        color: '#64748b',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        flexShrink: 0
      }}>
        Filters:
      </span>

      {/* Date quick-select */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={() => setDateLimit(null)}
          style={{
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: dateLimit === null ? 'rgba(0, 217, 255, 0.1)' : '#1a1f2e',
            border: `1px solid ${dateLimit === null ? '#00d9ff' : '#2d3748'}`,
            borderRadius: '6px',
            color: dateLimit === null ? '#00d9ff' : '#a0aec0',
            cursor: 'pointer'
          }}
        >
          All dates
        </button>
        {DATE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setDateLimit(opt.value)}
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: dateLimit === opt.value ? 'rgba(0, 217, 255, 0.1)' : '#1a1f2e',
              border: `1px solid ${dateLimit === opt.value ? '#00d9ff' : '#2d3748'}`,
              borderRadius: '6px',
              color: dateLimit === opt.value ? '#00d9ff' : '#a0aec0',
              cursor: 'pointer'
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ width: '1px', height: '18px', backgroundColor: '#2d3748', flexShrink: 0 }} />

      <MultiSelectDropdown label="League" options={leagueOpts} selected={selectedLeagues} onChange={setSelectedLeagues} />
      <MultiSelectDropdown label="Bookmaker" options={bookOpts} selected={selectedBooks} onChange={setSelectedBooks} />
      <MultiSelectDropdown label="Market" options={keytypeOpts} selected={selectedKeytypes} onChange={setSelectedKeytypes} />

      <div style={{ width: '1px', height: '18px', backgroundColor: '#2d3748', flexShrink: 0 }} />

      {/* My Books toggle */}
      <button
        onClick={handleMyBooksToggle}
        style={{
          padding: '5px 10px',
          backgroundColor: showOnlyMyBooks ? 'rgba(16, 185, 129, 0.1)' : '#1a1f2e',
          border: `1px solid ${showOnlyMyBooks ? '#10b981' : '#2d3748'}`,
          borderRadius: '6px',
          color: showOnlyMyBooks ? '#10b981' : '#a0aec0',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          whiteSpace: 'nowrap'
        }}
      >
        <span style={{ fontSize: '11px' }}>{showOnlyMyBooks ? '✓' : '○'}</span>
        My Books
      </button>

      {/* Prompt when no books are set */}
      {showPrompt && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '5px 10px',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#f59e0b'
        }}>
          No books set.{' '}
          <Link to="/account/sportsbooks" style={{ color: '#00d9ff', fontWeight: '600', textDecoration: 'none' }}>
            Set up
          </Link>
          <button
            onClick={() => setShowPrompt(false)}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0', fontSize: '12px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
      )}

      {hasActive && (
        <button
          onClick={() => {
            setSelectedLeagues([]);
            setSelectedBooks([]);
            setSelectedKeytypes([]);
            setDateLimit(null);
            setShowOnlyMyBooks(false);
            setShowPrompt(false);
          }}
          style={{
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            color: '#ef4444',
            cursor: 'pointer'
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default FilterBar;
